// src/components/dashboard/ChatScreen.tsx
//
// Renders useDashboardChat's `conversation` (raw Anthropic MessageParam[])
// into MessageScroller/Message/Bubble/Marker. Read-only render pass for
// message content — no invented message shape.
//
// UPDATED — real gated-tool approval UI. Previously rendered
// pendingApproval as an inert Marker notice with no way to act on it;
// confirmed live as a dead end when install_starter_kit (correctly
// gated, see _chatAutoApproved.ts) paused a real conversation with no
// path forward. resolveApproval already existed and worked on
// useDashboardChat — it just wasn't destructured or wired to anything
// here. PendingApprovalCard below replaces PendingApprovalNotice:
// same Marker-based visual language, now with Approve/Decline buttons
// wired to the real round-trip (POST /dashboard/chat with
// approvalDecision — chat.ts already handles this correctly, confirmed
// against real source).
//
// Tool input is rendered as pretty-printed JSON rather than a per-tool
// custom view — install_starter_kit is the first tool this path has
// actually been exercised against, and building bespoke review UI per
// gated tool (kit preview, message preview, domain preview, etc.) is
// real additional scope beyond making approval functional at all.
// Worth revisiting per-tool once more gated tools are actually used
// from chat in practice.
//
// editedInput is NOT wired in this pass — resolveApproval supports it,
// but there's no input editing UI here yet (e.g. no way to tweak
// kitId before approving). Approve always sends the tool's original
// input unmodified; Decline sends none. Editing support is a natural
// next step once a real need for it shows up (e.g. an approval that
// benefits from a quick correction rather than a full decline-and-retry).
//
// Chat history / new-chat: Surface A gets a Drawer (per the codebase's
// existing Drawer component and the IA doc's own "Marker triggers a
// Drawer for input collection" pattern) rather than a sidebar destination
// or header dropdown — keeps chat as the single spine entry point, with
// history as a sub-surface of being in chat, not a peer nav item.
//
// Resume-on-mount: if no initialChatId is supplied (i.e. navigated to
// plain /dashboard/chat), the most recently updated chat is loaded
// automatically via chats[0] once the list resolves. This only runs
// once per mount — subsequent chat switches happen via explicit user
// action (New chat / selecting a row in the history Drawer), never by
// this effect re-firing.
//
// onboarding prop — when true: uses useDashboardChat's 'onboarding'
// mode instead of 'owner', skips the resume-most-recent-chat effect
// entirely (onboarding always starts a fresh conversation — there's
// nothing to resume before an entity exists), hides history/new-chat
// controls (nothing to browse yet), and fires onEntityEstablished once
// the hook's entityEstablished flag flips true. DashboardShell owns
// what happens after that callback fires (currently: re-checking
// entity-status and swapping to the normal sidebar shell).

import * as React from "react"
import { useState, useEffect } from "react"
import ReactMarkdown from "react-markdown"
import {
  MessageScrollerProvider,
  MessageScroller,
  MessageScrollerViewport,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerButton,
} from "../ui/message-scroller"
import { MessageGroup, Message, MessageContent } from "../ui/message"
import { Bubble, BubbleContent } from "../ui/bubble"
import { Marker, MarkerIcon, MarkerContent } from "../ui/marker"
import { Button } from "../ui/button"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer"
import { Loader2Icon, InfoIcon, HistoryIcon, PlusIcon, CheckIcon, XIcon } from "lucide-react"
import {
  useDashboardChat,
  type ChatMessage,
} from "../../hooks/useDashboardChat"

// ---- content-block rendering -------------------------------------

// Strips emoji from model output as a hard guarantee, since the system
// prompt instruction (chat.ts) is a request the model can still miss —
// confirmed happening live (a status-marker emoji slipped through).
// Broad Unicode ranges covering emoji/symbol/pictograph blocks, not an
// exhaustive Unicode-emoji-property implementation — sufficient for
// stripping decorative glyphs without touching normal punctuation/text.
  function stripEmoji(text: string): string {
  return text.replace(
    /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}]/gu,
    ""
  )
}

  function TextBlockBubble({ text, align }: { text: string; align: "start" | "end" }) {
  const cleaned = stripEmoji(text)
  if (!cleaned.trim()) return null
  return (
    <Bubble align={align} variant={align === "end" ? "default" : "secondary"}>
      <BubbleContent>
        <div className="prose prose-sm dark:prose-invert max-w-none text-inherit *:text-inherit [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown>{cleaned}</ReactMarkdown>
        </div>
      </BubbleContent>
    </Bubble>
  )
}

function ToolUseMarker({ block }: { block: { name: string; input: any } }) {
  return (
    <Marker variant="border">
      <MarkerIcon>
        <InfoIcon />
      </MarkerIcon>
      <MarkerContent>
        Called <code>{block.name}</code>
      </MarkerContent>
    </Marker>
  )
}

function ToolResultMarker({ content }: { content: string }) {
  let summary = content
  try {
    const parsed = JSON.parse(content)
    if (parsed?.declined) {
      summary = "Declined by human."
    } else if (parsed?.error) {
      summary = `Error: ${parsed.error}`
    } else {
      summary = "Completed."
    }
  } catch {
    // leave summary as raw content if not JSON
  }
  return (
    <Marker>
      <MarkerContent className="text-xs">{summary}</MarkerContent>
    </Marker>
  )
}

function renderContent(msg: ChatMessage, align: "start" | "end") {
  if (typeof msg.content === "string") {
    return <TextBlockBubble text={msg.content} align={align} />
  }
  if (!Array.isArray(msg.content)) return null

  return (
    <>
      {msg.content.map((block: any, i: number) => {
        if (block.type === "text") {
          return <TextBlockBubble key={i} text={block.text} align={align} />
        }
        if (block.type === "tool_use") {
          return <ToolUseMarker key={i} block={block} />
        }
        if (block.type === "tool_result") {
          const raw =
            typeof block.content === "string"
              ? block.content
              : JSON.stringify(block.content)
          return <ToolResultMarker key={i} content={raw} />
        }
        return null
      })}
    </>
  )
}

// ---- pending approval — real, actionable card ------------------------

function PendingApprovalCard({
  name,
  input,
  isResolving,
  onApprove,
  onDecline,
}: {
  name: string
  input: any
  isResolving: boolean
  onApprove: () => void
  onDecline: () => void
}) {
  return (
    <Marker variant="border" className="text-foreground">
      <MarkerIcon>
        <InfoIcon />
      </MarkerIcon>
      <MarkerContent className="w-full">
        <div className="space-y-3">
          <p>
            Waiting on your approval for <code>{name}</code> before this continues.
          </p>

          {input != null && Object.keys(input).length > 0 && (
            <pre className="rounded-md bg-muted px-3 py-2 text-xs overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(input, null, 2)}
            </pre>
          )}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onApprove}
              disabled={isResolving}
              className="flex items-center gap-1.5"
            >
              {isResolving ? <Loader2Icon className="size-4 animate-spin" /> : <CheckIcon className="size-4" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onDecline}
              disabled={isResolving}
              className="flex items-center gap-1.5"
            >
              <XIcon className="size-4" />
              Decline
            </Button>
          </div>
        </div>
      </MarkerContent>
    </Marker>
  )
}

// ---- history drawer --------------------------------------------------

function ChatHistoryDrawer({
  open,
  onOpenChange,
  chats,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  chats: Array<{ id: string; title: string; updatedAt: string | null }>
  onSelect: (id: string) => void
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Your conversations</DrawerTitle>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto p-4 pt-0">
          {chats.length === 0 && (
            <p className="text-sm text-muted-foreground">No conversations yet.</p>
          )}
          <ul className="space-y-1">
            {chats.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => {
                    onSelect(c.id)
                    onOpenChange(false)
                  }}
                  className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                >
                  <div className="truncate font-medium">{c.title}</div>
                  {c.updatedAt && (
                    <div className="text-xs text-muted-foreground">
                      {new Date(c.updatedAt).toLocaleString()}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

// ---- screen ---------------------------------------------------------

interface ChatScreenProps {
  initialChatId?: string
  onboarding?: boolean
  onEntityEstablished?: () => void
}

export function ChatScreen({ initialChatId, onboarding = false, onEntityEstablished }: ChatScreenProps) {
  const {
    conversation,
    chatId,
    pendingApproval,
    isLoading,
    error,
    chats,
    refreshChats,
    startNewChat,
    sendMessage,
    resolveApproval,
    loadChat,
    entityEstablished,
  } = useDashboardChat(initialChatId, { mode: onboarding ? 'onboarding' : 'owner' })

  const [draft, setDraft] = useState("")
  const [isResuming, setIsResuming] = useState(!initialChatId && !onboarding)
  const [historyOpen, setHistoryOpen] = useState(false)

  // Load the chat list once on mount — skipped entirely in onboarding
  // mode, since there's nothing to resume before an entity exists.
  useEffect(() => {
    if (onboarding || initialChatId) {
      setIsResuming(false)
      return
    }
    let cancelled = false
    ;(async () => {
      await refreshChats()
      if (!cancelled) setIsResuming(false)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Resume the most recent chat once the list resolves — same guard as
  // before, plus never runs in onboarding mode.
  useEffect(() => {
    if (onboarding) return
    if (!isResuming && !chatId && !initialChatId && chats.length > 0) {
      loadChat(chats[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chats])

  // Onboarding completion — fires once, the moment the backend reports
  // the entity now exists.
  useEffect(() => {
    if (onboarding && entityEstablished) {
      onEntityEstablished?.()
    }
  }, [onboarding, entityEstablished, onEntityEstablished])

  const handleSend = () => {
    const text = draft.trim()
    if (!text || isLoading) return
    setDraft("")
    sendMessage(text)
  }

  const handleOpenHistory = async () => {
    await refreshChats()
    setHistoryOpen(true)
  }

  if (isResuming) {
    return (
      <div className="flex size-full min-h-0 items-center justify-center text-sm text-muted-foreground">
        Loading your conversation…
      </div>
    )
  }

  return (
    <MessageScrollerProvider>
      <div className="flex size-full min-h-0 flex-col">
        {!onboarding && (
          <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2">
            <span className="text-sm font-medium">Chat</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={startNewChat}
                aria-label="New chat"
              >
                <PlusIcon />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleOpenHistory}
                aria-label="Chat history"
              >
                <HistoryIcon />
              </Button>
            </div>
          </div>
        )}

        <MessageScroller className="flex-1">
          <MessageScrollerViewport>
            <MessageScrollerContent className="px-4 py-6">
              {conversation.map((msg, i) => {
                const align = msg.role === "user" ? "end" : "start"
                return (
                  <MessageScrollerItem key={i}>
                    <MessageGroup>
                      <Message align={align}>
                        <MessageContent>
                          {renderContent(msg, align)}
                        </MessageContent>
                      </Message>
                    </MessageGroup>
                  </MessageScrollerItem>
                )
              })}

              {pendingApproval && (
                <MessageScrollerItem>
                  <PendingApprovalCard
                    name={pendingApproval.name}
                    input={pendingApproval.input}
                    isResolving={isLoading}
                    onApprove={() => resolveApproval(true)}
                    onDecline={() => resolveApproval(false)}
                  />
                </MessageScrollerItem>
              )}

              {error && (
                <MessageScrollerItem>
                  <Marker variant="border" className="text-destructive">
                    <MarkerContent>{error}</MarkerContent>
                  </Marker>
                </MessageScrollerItem>
              )}
            </MessageScrollerContent>
          </MessageScrollerViewport>
          <MessageScrollerButton />
        </MessageScroller>

        <div className="flex shrink-0 items-end gap-2 border-t border-border p-3">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            placeholder="Message FDW..."
            rows={1}
            disabled={isLoading || !!pendingApproval}
            className="max-h-40 min-h-9 flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
          />
          <Button
            onClick={handleSend}
            disabled={isLoading || !draft.trim() || !!pendingApproval}
            size="sm"
          >
            {isLoading ? <Loader2Icon className="animate-spin" /> : "Send"}
          </Button>
        </div>
      </div>

      {!onboarding && (
        <ChatHistoryDrawer
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          chats={chats}
          onSelect={loadChat}
        />
      )}
    </MessageScrollerProvider>
  )
}